import React from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useUser } from "../providers/user";
import { GOOGLE_SHEETS_SCOPE } from "./secrets";

const LoginForm = () => {
  const { login: setUserLogin, setCurrentUser } = useUser();

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // With implicit flow, access_token is directly available
        if (!tokenResponse.access_token) {
          throw new Error('No access token received');
        }

        // Get user info using the access token
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          }
        );

        if (!userInfoResponse.ok) {
          throw new Error(`Failed to get user info: ${userInfoResponse.status}`);
        }

        const userInfo = await userInfoResponse.json();
        console.log("User Info:", userInfo);

        // Save user info WITH the access token
        const userData = {
          name: userInfo.name,
          email: userInfo.email,
          picture: userInfo.picture,
          accessToken: tokenResponse.access_token,
          expiresIn: tokenResponse.expires_in || 3600, // Usually 3600 seconds (1 hour)
          tokenType: tokenResponse.token_type,
          scope: tokenResponse.scope,
          loginTime: Date.now(), // Track when token was issued
        };

        console.log("Setting currentUser with:", userData);

        // Save to context
        setCurrentUser(userData);
        setUserLogin(userData);

        // Save to localStorage for persistence
        localStorage.setItem('financeAppUser', JSON.stringify(userData));
      } catch (err) {
        console.error("Failed to get user info:", err);
      }
    },
    onError: (error) => {
      console.error("Login Failed:", error);
    },
    scope: GOOGLE_SHEETS_SCOPE,
    // Remove flow: 'auth-code' to use implicit flow (default)
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md text-center">
        <div className="mb-6">
          <h1 className="text-4xl font-bold mb-2">💰 Finance Tracker </h1>
          <p className="text-gray-600 mt-2">
            Track your expenses with Google Sheets
          </p>
        </div>

        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This app will request access to your Google Sheets to save your expenses.
          </p>
        </div>

        <button
          onClick={() => login()}
          className="w-full flex items-center justify-center gap-3 bg-white border-2 border-gray-300 rounded-xl px-6 py-4 text-gray-700 font-semibold hover:bg-gray-50 hover:border-blue-500 transition-all duration-200 shadow-md hover:shadow-lg"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </button>

        <p className="text-xs text-gray-500 mt-6">
          By signing in, you agree to grant access to Google Sheets
        </p>

        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs text-green-700">
            ✅ Your session will automatically refresh to keep you logged in!
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
